import { useQuery } from '@tanstack/react-query';

import type { LeadListFilters } from '../types';
import { leadsApi } from './client';
import { leadKeys } from './keys';

/**
 * useLeadList — paginated list of leads, optionally filtered by status.
 *
 * Default page size mirrors the DataTable default (10). Pass `status` to use
 * the `/Lead/ByStatus` endpoint; omit it for the base listing.
 */
export function useLeadList(filters: LeadListFilters = {}) {
  const merged: LeadListFilters = {
    pageNumber: filters.pageNumber ?? 1,
    pageSize: filters.pageSize ?? 100,
    status: filters.status,
  };
  return useQuery({
    queryKey: leadKeys.list(merged),
    queryFn: () => leadsApi.list(merged),
  });
}

/**
 * useLead — single lead by id, resolved from the cached list.
 *
 * The backend doesn't expose a dedicated `/Lead/:id` endpoint, so we read
 * straight from the list cache (legacy behaviour). When the list hasn't been
 * fetched yet this triggers it via the underlying `useLeadList` query.
 */
export function useLead(id: string | undefined) {
  const list = useLeadList({ pageSize: 200 });
  const data = id ? list.data?.items.find((lead) => lead.id === id) : undefined;
  return {
    ...list,
    data,
    /** True when the list has resolved and the id wasn't found. */
    notFound: list.isSuccess && !!id && !data,
  };
}
